// ===== Layer 2: 카테고리 설정 (의존성: base.ts만) =====

import type { CodeType, ItemSearchFilters, CustomerSearchFilters, InspectPopupSearchFilters } from 'src/types/base';

// API 함수들 import
import { getItemPopupList } from 'src/apis/itemApi';
import { getCustomerList } from 'src/apis/customerApi';
import { getOrganizationList } from 'src/apis/organizaApi';
import { getEmployeeList } from 'src/apis/employeeApi';
import { getUserBySearch } from 'src/apis/authApi';
import { getMenuModalList } from 'src/apis/menuApi';
import { getMinorCodeList } from 'src/apis/commonCodeApi';
import { getActiveWarehouses } from 'src/apis/warehouseApi';
import { getWorkPlaceList } from 'src/apis/workPlaceApi';
import { getBankacctList } from 'src/apis/bankacctApi';
import { getEquipmentPopupList } from 'src/apis/equipmentApi';
import { getInspectPopupList } from 'src/apis/inspectApi';
import { getCompanyList } from 'src/apis/companyApi';

// 렌더러 설정 인터페이스
export interface RendererConfig {
	type: 'commonCode';
	listKey: string; // commonCodeStore에서 가져올 리스트의 키 (예: 'unitList', 'itemAccountList')
	codeField?: string; // 코드 필드명 (기본값: 'minorCd')
	nameField?: string; // 이름 필드명 (기본값: 'codeNm')
}

// 컬럼 설정 인터페이스
export interface ColumnDefinition {
	headerName: string;
	field: string;
	flex?: number;
	width?: number;
	cellRenderer?: string;
	rendererConfig?: RendererConfig; // 렌더러 설정 (방안 3)
}

// 필터 설정 인터페이스
export interface FilterDefinition {
	key: string;
	label: string;
	dataKey?: string;
}

// 모달 크기 설정 인터페이스
export interface ModalSizeOptions {
	size?: 'xs' | 'sm' | 'md' | 'lg' | 'half' | 'mmd';
	width?: number;  // 퍼센트 단위 (기본값: 70)
	// height 제거 - size로 자동 결정
}

// 공통코드 스토어 타입 (순환 참조 방지를 위한 타입 정의)
export type CommonCodeStoreType = {
	unitList?: Array<{ minorCd: string; codeNm: string }>;
	itemAccountList?: Array<{ minorCd: string; codeNm: string }>;
	[key: string]: any;
};

// 카테고리 설정 인터페이스
export interface CategoryDefinition {
	// 기본 정보
	type: CodeType;
	titles: readonly string[];
	
	// API 설정
	apiFunction: (...args: any[]) => Promise<any>;
	codeField: string;
	nameField?: string;
	getApiParams: (searchText: string, filters: any) => any[];
	transformResponse?: (data: any[]) => any[];
	
	// UI 설정
	searchFields: readonly string[];
	columns: readonly ColumnDefinition[] | ((commonCodeStore?: CommonCodeStoreType) => ColumnDefinition[]);
	filterFields?: readonly FilterDefinition[];
	hasFilters?: boolean;
	
	// 모달 크기 설정
	modalSize?: ModalSizeOptions;
}

// ===== 모든 카테고리 설정 (한 곳에 집중) =====
export const CATEGORIES: readonly CategoryDefinition[] = [
	// 품목
	{
		type: 'item',
		titles: ['품목'] as const,
		
		// API 설정
		apiFunction: getItemPopupList,
		codeField: 'itemCd',
		nameField: 'itemNm',
		getApiParams: (searchText: string, filters: ItemSearchFilters) => [
			searchText,
			filters.itemAccount || 'all',
			filters.itemGroup || 'all',
			filters.brandCd || 'all'
		],
		
		// UI 설정
		searchFields: ['itemNm', 'itemCd', 'modelNo', 'itemSpec', 'itemUnit'] as const,
		hasFilters: false,
		filterFields: [] as const,
		columns: (commonCodeStore?: CommonCodeStoreType) => {
			const baseColumns: ColumnDefinition[] = [
				{ headerName: '코드', field: 'itemCd', flex: 1 },
				{ headerName: '품명', field: 'itemNm', flex: 2 },
			];
			
			return baseColumns;
		},
		
		// 모달 크기 설정 - 필터가 많아서 넓게
		modalSize: {
			size: 'lg',
			width: 80
		}
	},
	
	// 거래처
	{
		type: 'customer',
		titles: ['거래처'] as const,
		
		// API 설정
		apiFunction: getCustomerList,
		codeField: 'customCd',
		nameField: 'customNm',
		getApiParams: (searchText: string, filters: CustomerSearchFilters) => [
			filters.areaCd || 'all',
			searchText || 'all'
		],
		
		// UI 설정
		searchFields: ['customNm', 'customCd'] as const,
		hasFilters: false,
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: '거래처', field: 'customCd', flex: 1 },
			{ headerName: '상호', field: 'customNm', flex: 2 },
			{ headerName: '대표자', field: 'owner', flex: 1 },
			{ headerName: '전화번호', field: 'telNo', flex: 2 },
			{ headerName: '주소', field: 'address', flex: 2 }
		] as const,
		
		// 모달 크기 설정 - 중간 크기
		modalSize: {
			size: 'md',
			width: 50
		}
	},
	
	// 조직
	{
		type: 'organization',
		titles: ['부서', '조직', '상위부서', '통합팀'] as const,
		
		// API 설정
		apiFunction: getOrganizationList,
		codeField: 'orgCd',
		nameField: 'orgNm',
		getApiParams: (searchText: string) => [searchText],
		
		// UI 설정
		searchFields: ['orgNm', 'orgCd'] as const,
		hasFilters: false,
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: '부서코드', field: 'orgCd', width: 100 },
			{ headerName: '부서명', field: 'orgNm', flex: 1 }
		] as const,
		
		// 모달 크기 설정 - 간단한 구조로 작게
		modalSize: {
			size: 'sm',
			width: 60
		}
	},
	
	// 사원
	{
		type: 'employee',
		titles: ['부서장', '사원번호', '사원', '작업자', '담당자', '발주자', '시험자','등록자'] as const,
		
		// API 설정
		apiFunction: getEmployeeList,
		codeField: 'empNo',
		nameField: 'empNm',
		getApiParams: (searchText: string) => [searchText],
		
		// UI 설정
		searchFields: ['empNo', 'empNm'] as const,
		hasFilters: false,
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: '사원번호', field: 'empNo', width: 100 },
			{ headerName: '사원명', field: 'empNm', flex: 1 }
		] as const,
		
		// 모달 크기 설정 - 간단한 구조로 작게
		modalSize: {
			size: 'sm',
			width: 20
		}
	},
	
	// 사용자
	{
		type: 'user',
		titles: ['사용자'] as const,
		
		// API 설정
		apiFunction: getUserBySearch,
		codeField: 'userId',
		nameField: 'userNm',
		getApiParams: (searchText: string) => [searchText],
		transformResponse: (data: any[]) => data.map(item => ({
			userId: item.userId,
			userNm: item.userNm,
			empNo: item.empNo,
			empNm: item.empNm || '',
		})),
		
		// UI 설정
		searchFields: ['userId', 'userNm', 'empNo', 'empNm'] as const,
		hasFilters: false,
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: '사용자ID', field: 'userId', flex: 1 },
			{ headerName: '사용자명', field: 'userNm', flex: 1 },
			{ headerName: '사원번호', field: 'empNo', flex: 1 },
			{ headerName: '직원명', field: 'empNm', flex: 1 }
		] as const,
		
		// 모달 크기 설정 - 중간 크기
		modalSize: {
			size: 'md',
			width: 50
		}
	},
	
	// 메뉴
	{
		type: 'menu',
		titles: ['메뉴', '상위메뉴코드'] as const,
		
		// API 설정
		apiFunction: getMenuModalList,
		codeField: 'menuCd',
		nameField: 'menuNm',
		getApiParams: (searchText: string) => [searchText],
		
		// UI 설정
		searchFields: ['menuNm', 'menuCd'] as const,
		hasFilters: false,
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: '메뉴코드', field: 'menuCd', width: 100 },
			{ headerName: '메뉴명', field: 'menuNm', flex: 1 }
		] as const,
		
		// 모달 크기 설정 - 간단한 구조로 작게
		modalSize: {
			size: 'sm',
			width: 60
		}
	},
	
	// 메뉴권한
	{
		type: 'menuAuth',
		titles: ['메뉴권한'] as const,
		
		// API 설정
		apiFunction: getMinorCodeList,
		codeField: 'minorCd',
		nameField: 'codeNm',
		getApiParams: (searchText: string) => ['AUTGROUP', searchText],
		transformResponse: (data: any[]) => data.map(item => ({
			minorCd: item.minorCd,
			codeNm: item.codeNm,
		})),
		
		// UI 설정
		searchFields: ['codeNm', 'minorCd'] as const,
		hasFilters: false,
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: '권한코드', field: 'minorCd', width: 100 },
			{ headerName: '권한명', field: 'codeNm', flex: 1 }
		] as const,
		
		// 모달 크기 설정 - 간단한 구조로 작게
		modalSize: {
			size: 'sm',
			width: 60
		}
	},
	// 공정코드
	{
		type: 'workplace',
		titles: ['공정코드'] as const,
		
		// API 설정
		apiFunction: getWorkPlaceList,
		codeField: 'workplaceCd',
		nameField: 'workplaceNm',
		getApiParams: (searchText: string) => [searchText],
		transformResponse: (data: any[]) => data.map((item, index) => ({
			rowIndex: index + 1, 
			workplaceCd: item.workplaceCd,
			workplaceNm: item.workplaceNm,
		})),
		
		// UI 설정
		searchFields: ['workplaceNm', 'workplaceCd'] as const,
		hasFilters: false,
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: '공정코드', field: 'workplaceCd', width: 100 },
			{ headerName: '공정명', field: 'workplaceNm', flex: 1 }
		] as const,
		
		// 모달 크기 설정 - 간단한 구조로 작게
		modalSize: {
			size: 'sm',
			width: 60
		}
	},
	// 창고
	{
		type: 'warehouse',
		titles: ['창고코드'] as const,
		
		// API 설정
		apiFunction: getActiveWarehouses, // 사용 가능한 창고만 조회
		codeField: 'warehouseCd',
		nameField: 'warehouseNm',
		getApiParams: () => [], // 파라미터 없음
		transformResponse: (data: any[]) => data.map((item, index) => ({
			rowIndex: index + 1, 
			warehouseCd: item.warehouseCd,
			warehouseNm: item.warehouseNm,
			logicCenter: item.logicCenter, // 물류센터 정보 포함
		})),
		
		// UI 설정
		searchFields: ['warehouseNm', 'warehouseCd'] as const,
		hasFilters: false,
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: '창고코드', field: 'warehouseCd', width: 100 },
			{ headerName: '창고명', field: 'warehouseNm', flex: 1 }
		] as const,
		
		// 모달 크기 설정 - 간단한 구조로 작게
		modalSize: {
			size: 'sm',
			width: 60
		}
	},	
	// 계좌
	{
		type: 'bankacct',
		titles: ['계좌코드', '계좌번호'] as const,
		
		// API 설정
		apiFunction: getBankacctList,
		codeField: 'bankAcctCd',
		nameField: 'bankAcctNm',
		getApiParams: (searchText: string, ) => [
			searchText || ''
		],
		
		// UI 설정
		searchFields: ['bankAcctNm', 'bankAcctCd'] as const,
		hasFilters: false,
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: '계좌코드', field: 'bankAcctCd', flex: 1 },
			{ headerName: '계좌명', field: 'bankAcctNm', flex: 2 },
			{ headerName: '계좌번호', field: 'bankAcctNo', flex: 2 },
			{ headerName: '은행', field: 'bankCd', flex: 2 },
		] as const,
		
		// 모달 크기 설정 - 중간 크기
		modalSize: {
			size: 'md',
			width: 50
		}
	},
	// 설비
	{
		type: 'equipment',
		titles: ['설비코드'] as const,
	
		apiFunction: getEquipmentPopupList,
		codeField: 'equipmentCd',
		nameField: 'equipmentNm',
		getApiParams: (searchText: string) => [searchText],
	
		searchFields: ['equipmentNm', 'equipmentCd'] as const,
		hasFilters: false,
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: '설비코드', field: 'equipmentCd', width: 100 },
			{ headerName: '설비명', field: 'equipmentNm', flex: 1 },
			{ headerName: '설비규격', field: 'equipmentSpec', flex: 1 },
			{ headerName: '설비유형', field: 'equipmentType', flex: 1 },
			
		] as const,
	
		modalSize: {
			size: 'sm',
			width: 60
		}
	},
	// 설비 점검 
	{
		type: 'inspect',
		titles: ['설비점검코드'] as const,
		
		// API 설정
		apiFunction: getInspectPopupList,
		codeField: 'inspectCd',
		nameField: 'inspectNm',
		getApiParams: (searchText: string, filters: InspectPopupSearchFilters) => [searchText, filters.inspectMethod || 'all', filters.inspectNm || 'all', filters.inspectType || 'all', filters.eqmType || 'all'],
		transformResponse: (data: any[]) => data.map(item => ({
			eqmType: item.eqmType,
			inspectCd: item.inspectCd,
			inspectNm: item.inspectNm,
			inspectMethod: item.inspectMethod,
			inspectType: item.inspectType,
			inspectValue: item.inspectValue,
			remark: item.remark,
		})),
		
		// UI 설정
		searchFields: ['inspectNm', 'inspectCd', 'inspectMethod', 'inspectType', 'eqmType'] as const,
		hasFilters: false,
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: '설비유형', field: 'eqmType', width: 100  },
			{ headerName: '점검코드', field: 'inspectCd', width: 100 },
			{ headerName: '점검명', field: 'inspectNm', flex: 1 },
			{ headerName: '점검방법', field: 'inspectMethod', flex: 1 },
			{ headerName: '점검유형', field: 'inspectType', flex: 1 },
			{ headerName: '결과유형', field: 'inspectValue', flex: 1 },
			{ headerName: '비고', field: 'remark', flex: 1 },
		] as const,
	},
	// 사업장 (SYS4010_R01)
	{
		type: 'company',
		titles: ['사업장', '사업장명'] as const,
		
		// API 설정
		apiFunction: getCompanyList,
		codeField: 'compCd',
		nameField: 'compNm',
		getApiParams: (searchText: string) => [searchText || ''],
		
		// UI 설정
		searchFields: ['compNm', 'compCd'] as const,
		hasFilters: false,
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: '사업장코드', field: 'compCd', width: 100 },
			{ headerName: '사업장명', field: 'compNm', flex: 1 }
		] as const,
		
		// 모달 크기 설정 - 간단한 구조로 작게
		modalSize: {
			size: 'sm',
			width: 60
		}
	}
] as const;

// ===== 타입 안전한 조회 함수들 =====

/**
 * 카테고리 타입으로 설정 조회 (O(1) 최적화)
 */
export const getCategoryByType = (type: CodeType): CategoryDefinition | undefined => {
	// 캐시된 매핑 테이블 사용 (O(1) 조회)
	const CATEGORY_MAP = getCategoryMap();
	return CATEGORY_MAP.get(type);
};

/**
 * 카테고리 매핑 테이블 (지연 생성)
 */
let categoryMapCache: Map<CodeType, CategoryDefinition> | null = null;
const getCategoryMap = (): Map<CodeType, CategoryDefinition> => {
	if (!categoryMapCache) {
		categoryMapCache = new Map();
		CATEGORIES.forEach(category => {
			categoryMapCache!.set(category.type, category);
		});
	}
	return categoryMapCache;
};

/**
 * 카테고리 제목으로 설정 조회
 */
export const getCategoryByTitle = (title: string): CategoryDefinition | undefined => {
	return CATEGORIES.find(category => category.titles.includes(title as any));
};
